import { BottomSheet } from "@/design-system/BottomSheet/BottomSheet";
import { Button } from "@/design-system/Button/Button";
import { Foldout } from "@/design-system/Foldout/Foldout";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import PlayerCard from "@/design-system/PlayerCard/PlayerCard";
import {
  FIRST_OWNER_GLYPH,
  PLAYER_CARD_BASE_HEIGHT,
  PLAYER_CARD_BASE_WIDTH,
} from "@/design-system/PlayerCard/PlayerCardView";
import { SearchBar } from "@/design-system/SearchBar/SearchBar";
import { SegmentedControl } from "@/design-system/SegmentedControl/SegmentedControl";
import { cn } from "@/lib/utils";
import { addAlert } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { BREAKPOINTS } from "@/settings";
import { safeGetJSON, safeSetItem } from "@/utils/safeStorage";
import { GALLERY_STORAGE_KEYS, grouped } from "@repo/shared/gallery";
import { useGalleryFirstOwners } from "@repo/shared/hooks";
import { useTranslation } from "@repo/shared/i18n";
import { Eye, EyeOff, Lightbulb } from "lucide-react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { CARD_SCALE } from "./GallerySolutionView";

// EA's club views: 6 or 10 per row on desktop, a fixed 6 on mobile (3 on phones).
// A count is used only while its cards stay at least MIN_SCALE.
const DESKTOP_COLUMNS = [6, 10] as const;
type Columns = 3 | 6 | 10;
const MIN_SCALE = 0.5;
// The grid's gap-2.
const GAP = 8;
// The box's p-2 and border around the card.
const BOX_EXTRA = 18;
// The guide starts closed and pulses until first toggled; after that it remembers its state.
/** A guide step's number; the set page's guide uses it too. */
export const StepNumber = ({
  n,
  className,
}: {
  n: number;
  className?: string;
}) => (
  <span
    className={cn(
      "flex size-5 shrink-0 items-center justify-center rounded-full border border-neutral-400 text-tiny font-semibold tabular-nums text-neutral-200",
      className,
    )}
  >
    {n}
  </span>
);

const GUIDE_STEPS = ["step1", "step2", "step3", "step4"] as const;

/** The user's collected cards for one set; clicking a card toggles its First Owner flag. */
export const GalleryFirstOwnerSheet = ({
  setId,
  setName,
  isOpen,
  onClose,
  onChanged,
  solutionIds,
}: {
  setId: number;
  setName: string;
  isOpen: boolean;
  onClose: () => void;
  /** After the sheet closes on a saved change: the backend now marks the solutions outdated. */
  onChanged?: () => void;
  /** The caller's lineup for the set: taken as inserted in game, so they can be hidden like EA's grid hides them. */
  solutionIds?: number[];
}) => {
  const { t } = useTranslation("gallery");
  const dispatch = useDispatch<AppDispatch>();
  // A callback ref: the sheet mounts its content only once open.
  const [width, setWidth] = useState(0);
  const contentRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const fitScale = (columns: number) =>
    ((width - (columns - 1) * GAP) / columns - BOX_EXTRA) /
    PLAYER_CARD_BASE_WIDTH;
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const options: readonly Columns[] = isMobile
    ? [fitScale(6) >= MIN_SCALE ? 6 : 3]
    : DESKTOP_COLUMNS;
  const available = options.filter(
    (n, i) => i === 0 || fitScale(n) >= MIN_SCALE,
  );
  const [pickedColumns, setPickedColumns] = useState<Columns>(10);
  const columns = available.includes(pickedColumns)
    ? pickedColumns
    : available[available.length - 1];
  // Cards fill the row; floored so rounding never pushes the last column over.
  const scale = Math.floor(fitScale(columns) * 1000) / 1000;
  const boxWidth = Math.floor(PLAYER_CARD_BASE_WIDTH * scale) + BOX_EXTRA;
  // The score pill and First Owner row shrink with the card.
  const zoom = scale / CARD_SCALE;
  const [hideInserted, setHideInserted] = useState(false);
  const {
    players,
    visible,
    error,
    search,
    setSearch,
    close,
    toggle,
    ...marks
  } = useGalleryFirstOwners({
    setId,
    onClose,
    onChanged,
    onSaveFailed: () =>
      dispatch(
        addAlert({ type: "error", description: t("common:error.generic") }),
      ),
    hiddenIds: hideInserted ? solutionIds : undefined,
  });
  const insertedCount =
    players?.filter((p) => solutionIds?.includes(p.resourceId)).length ?? 0;
  // Null until the guide is first toggled.
  const [guideOpen, setGuideOpen] = useState(() =>
    safeGetJSON<boolean>(GALLERY_STORAGE_KEYS.firstOwnerGuideOpen),
  );

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={close}
      className="h-[85dvh] max-w-full bg-neutral-900"
      header={{
        title: t("firstOwners.title", { name: setName }),
        closeLabel: t("firstOwners.close"),
      }}
    >
      <div
        ref={contentRef}
        className="mx-auto w-full max-w-content px-4 pb-6 large:px-16 touch-device:px-4"
      >
        <div className="mb-2">
          <Foldout
            label={
              <span className={cn(guideOpen === null && "animate-pulse")}>
                {t("firstOwners.guideTitle")}
              </span>
            }
            open={!!guideOpen}
            onToggle={() => {
              setGuideOpen(!guideOpen);
              safeSetItem(
                GALLERY_STORAGE_KEYS.firstOwnerGuideOpen,
                JSON.stringify(!guideOpen),
              );
            }}
          >
            <div className="flex max-w-3xl flex-col gap-y-3 text-sm">
              <ol className="flex flex-col gap-y-2">
                {GUIDE_STEPS.map((step, i) => (
                  <li key={step} className="flex items-start gap-x-2">
                    <StepNumber n={i + 1} />
                    <span>{t(`firstOwners.${step}`, { name: setName })}</span>
                  </li>
                ))}
              </ol>
              <p className="text-foreground-muted">
                <span className="font-semibold text-foreground">
                  {t("firstOwners.tip")}:
                </span>{" "}
                {t("firstOwners.tipLeagues")}
              </p>
              <p className="flex items-start gap-x-2 rounded-lg border border-amber-500/50 bg-amber-900/30 p-3 font-semibold text-amber-200">
                <Lightbulb className="mt-px size-4 shrink-0 text-amber-400" />
                <span>{t("firstOwnersGuide.bestText")}</span>
              </p>
            </div>
          </Foldout>
        </div>
        {/* Stays at the top while the cards scroll under it; padding, not margin, so the sheet's colour covers them. */}
        <div className="sticky top-0 z-20 mb-2 flex flex-wrap items-center gap-x-2 gap-y-2 bg-neutral-900 py-2">
          <div className="w-96 min-w-0">
            <SearchBar size="small" value={search} onChange={setSearch} />
          </div>
          {available.length > 1 && (
            <SegmentedControl
              label={t("firstOwners.perRow")}
              options={available.map((n) => ({
                value: String(n),
                label: String(n),
              }))}
              value={String(columns)}
              onChange={(value) => setPickedColumns(Number(value) as Columns)}
              // Button size small's h-8; the segments and pill shrink to fit.
              className="h-8 shrink-0 p-0.5 [&>button]:py-1 [&>button]:text-xs [&>span]:inset-y-0.5"
            />
          )}
          {insertedCount > 0 && (
            // The row's h-8 pill; on, it reads like a pressed segment.
            <button
              type="button"
              aria-pressed={hideInserted}
              onClick={() => setHideInserted(!hideInserted)}
              className={cn(
                "flex h-8 shrink-0 items-center gap-x-1.5 whitespace-nowrap rounded-[20px] border px-3 text-sm transition-colors",
                hideInserted
                  ? "border-neutral-500 bg-neutral-700 text-neutral-50"
                  : "border-neutral-700 bg-neutral-800 text-neutral-300 hover:border-neutral-600 hover:text-neutral-100",
              )}
            >
              {hideInserted ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
              {t("firstOwners.hideInserted")}
              <span className="tabular-nums text-neutral-500">
                {insertedCount}
              </span>
            </button>
          )}
          <Button
            variant="secondary"
            size="small"
            className="ml-auto shrink-0 whitespace-nowrap"
            disabled={!marks.canMarkAll}
            onClick={marks.markAll}
          >
            {t("firstOwners.markAll")}
          </Button>
        </div>
        {!width ? null : error ? (
          <p className="text-sm text-red-400">{t("firstOwners.error")}</p>
        ) : players && !players.length ? (
          <p className="text-sm text-foreground-muted">
            {t("firstOwners.empty")}
          </p>
        ) : (
          <ul
            className="grid justify-center gap-2"
            style={{
              gridTemplateColumns: `repeat(${columns}, ${boxWidth}px)`,
            }}
          >
            {!players
              ? Array.from({ length: 6 }, (_, i) => (
                  <li
                    key={i}
                    className="animate-pulse rounded-[20px] bg-neutral-800"
                    // Card plus the box's padding and the First Owner row.
                    style={{
                      height: PLAYER_CARD_BASE_HEIGHT * scale + 18 + 26 * zoom,
                    }}
                  />
                ))
              : visible?.map((collected) => {
                  const { resourceId, firstOwner, player } = collected;
                  const inserted = solutionIds?.includes(resourceId);
                  return (
                    <li key={resourceId}>
                      {/* The whole box toggles the flag. */}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={firstOwner}
                        aria-label={`${t("solution.firstOwner")} ${player.name}`}
                        onClick={() => toggle(collected)}
                        className="relative flex w-full flex-col items-center rounded-[20px] border border-neutral-700 bg-neutral-800 p-2 transition-colors duration-200 hover:border-neutral-600 hover:bg-neutral-750 focus-visible:outline-none"
                      >
                        {inserted && (
                          <span
                            title={t("firstOwners.inSolution")}
                            aria-label={t("firstOwners.inSolution")}
                            className="absolute right-2.5 top-2.5 size-1.5 rounded-full bg-neutral-400"
                          />
                        )}
                        {/* The item score rests on the card's top edge, like the solution page's pill. */}
                        <div className="relative">
                          <PlayerCard
                            player={player}
                            position={player.preferredPosition}
                            scale={scale}
                            hidePrice
                            firstOwner={firstOwner}
                          />
                          {player.itemScore !== undefined && (
                            <div
                              className="absolute inset-x-0 top-2 flex justify-center"
                              style={{ zoom }}
                            >
                              <div className="flex min-h-6 items-center gap-x-1 rounded-lg border border-neutral-700 bg-neutral-900 px-1.5 text-sm tabular-nums">
                                <ItemScoreDiamond size={12} />
                                <span className="font-oswald">
                                  {grouped.format(player.itemScore)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        <div
                          className={cn(
                            "-mt-1.5 flex w-full items-center justify-center gap-x-1.5 border-t border-neutral-700 pt-2",
                            firstOwner
                              ? "text-streamline-success"
                              : "text-neutral-600",
                          )}
                          style={{ zoom }}
                        >
                          <span
                            className="text-lg leading-none"
                            style={{ fontFamily: "UltimateTeamIcons" }}
                          >
                            {FIRST_OWNER_GLYPH}
                          </span>
                          <span
                            className="whitespace-nowrap text-sm uppercase leading-none tracking-wide"
                            style={{ fontFamily: "CruyffCondensedRegular" }}
                          >
                            {t("solution.firstOwner")}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
          </ul>
        )}
      </div>
    </BottomSheet>
  );
};
